import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  buscarPorCedula,
  getsolicitudes,
} from "../../../helpers/backend_helper";
import {
  parsearCSVSolicitud,
  validarArchivoCSV,
  leerArchivoComoTexto,
} from "../../../helpers/csvParser";

const DEBOUNCE_MS = 600;
let debounceTimer = null;
let debounceIdTimer = null;

const LENGTH_RULES = {
  identificador: { min: 3, max: 50 },
  solicitanteCedula: { min: 2, max: 15 },
  titularCedula: { min: 2, max: 15 },
  titular: { min: 3, max: 255 },
  solicitante: { min: 3, max: 255 },
  telefono: { exact: 11 },
};

const PasoPaciente = ({ datos, actualizarDatos, onSiguiente }) => {
  const [errores, setErrores] = useState({});
  const [buscando, setBuscando] = useState(false);
  const [busquedaMsg, setBusquedaMsg] = useState(null);
  const [cedulaIngresada, setCedulaIngresada] = useState(
    !!datos.solicitanteCedula,
  );
  const [verificandoId, setVerificandoId] = useState(false);
  const [idDuplicado, setIdDuplicado] = useState(false);
  const [idMsg, setIdMsg] = useState(null);
  // Estados para carga de CSV
  const [csvFile, setCsvFile] = useState(null);
  const [cargandoCSV, setCargandoCSV] = useState(false);
  const [resumenCSV, setResumenCSV] = useState(null);
  const [alertaCSVIdDuplicado, setAlertaCSVIdDuplicado] = useState(null);

  const ejecutarBusqueda = useCallback(
    async (cedula) => {
      if (!cedula || cedula.trim().length < 4) {
        setBusquedaMsg(null);
        return;
      }
      setBuscando(true);
      setBusquedaMsg(null);
      try {
        const res = await buscarPorCedula(cedula.trim());
        const body = res?.data ?? res;
        if (body?.found && body.data) {
          const d = body.data;
          const telefonoLimpio = (d.telefono || "")
            .replace(/\D/g, "")
            .slice(0, 11);
          actualizarDatos({
            titular: (d.titular || "").trim(),
            titularCedula: (d.titularCedula || "").trim(),
            solicitante: (d.solicitante || "").trim(),
            solicitanteCedula: cedula.trim(),
            correoElectronico: (d.correoElectronico || "").trim(),
            telefono: telefonoLimpio,
            direccion: (d.direccion || "").trim(),
            recibe: (d.recibe || "").trim(),
          });
          const camposVacios = [
            !d.titular?.trim() && "Nombre Titular",
            !d.titularCedula?.trim() && "Cédula Titular",
            !telefonoLimpio && "Teléfono",
          ].filter(Boolean);
          if (camposVacios.length > 0) {
            setBusquedaMsg({
              tipo: "ok",
              texto: `Solicitante encontrado. Complete los campos pendientes: ${camposVacios.join(", ")}.`,
            });
          } else {
            setBusquedaMsg({
              tipo: "ok",
              texto: "Solicitante encontrado. Datos completados.",
            });
          }
        } else {
          actualizarDatos({ solicitanteCedula: cedula.trim() });
          setBusquedaMsg({
            tipo: "info",
            texto:
              "No se encontraron registros previos. Complete los datos manualmente.",
          });
        }
      } catch {
        setBusquedaMsg({
          tipo: "error",
          texto: "Error al buscar. Complete los datos manualmente.",
        });
      } finally {
        setBuscando(false);
        setCedulaIngresada(true);
      }
    },
    [actualizarDatos],
  );

  const handleCedulaChange = (e) => {
    const val = e.target.value
      .replace(/[^a-zA-Z0-9-]/g, "")
      .slice(0, LENGTH_RULES.solicitanteCedula.max);
    actualizarDatos({ solicitanteCedula: val });
    if (!val || val.length < 4) {
      setCedulaIngresada(false);
      setBusquedaMsg(null);
      return;
    }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => ejecutarBusqueda(val), DEBOUNCE_MS);
  };

  const verificarIdentificador = useCallback(async (valor) => {
    if (!valor || valor.length < LENGTH_RULES.identificador.min) {
      setIdDuplicado(false);
      setIdMsg(null);
      return;
    }
    setVerificandoId(true);
    setIdMsg(null);
    try {
      const { data } = await getsolicitudes(
        `query=${encodeURIComponent(valor)}`,
      );
      const lista = data?.data;
      const coincidencias = Array.isArray(lista) && lista[0] ? lista[0] : [];
      if (Array.isArray(coincidencias) && coincidencias.length > 0) {
        setIdDuplicado(true);
        setIdMsg({ tipo: "error", texto: `El ID ${valor} ya está registrado` });
      } else {
        setIdDuplicado(false);
        setIdMsg({ tipo: "ok", texto: "ID disponible" });
      }
    } catch (err) {
      setIdDuplicado(true);
      setIdMsg({
        tipo: "error",
        texto: "No se pudo validar el ID. Intente de nuevo.",
      });
    } finally {
      setVerificandoId(false);
    }
  }, []);

  const handleIdentificadorChange = (e) => {
    const raw = e.target.value.slice(0, LENGTH_RULES.identificador.max);
    actualizarDatos({ identificador: raw });
    setIdDuplicado(false);
    setIdMsg(null);
    const trimmed = raw.trim();
    if (!trimmed || trimmed.length < LENGTH_RULES.identificador.min) {
      if (debounceIdTimer) clearTimeout(debounceIdTimer);
      return;
    }
    if (debounceIdTimer) clearTimeout(debounceIdTimer);
    debounceIdTimer = setTimeout(
      () => verificarIdentificador(trimmed),
      DEBOUNCE_MS,
    );
  };

  const camposBloqueados = !cedulaIngresada || idDuplicado;

  const NOMBRES_CAMPOS = {
    identificador: "ID Solicitud Mercantil",
    solicitanteCedula: "Cédula del Solicitante",
    tipo: "Tipo de Solicitud",
    titular: "Nombre del Titular",
    titularCedula: "Cédula del Titular",
    solicitante: "Nombre del Solicitante",
    correoElectronico: "Correo Electrónico",
    telefono: "Teléfono",
  };

  const validar = () => {
    const e = {};
    const trimmedId = datos.identificador?.trim() || "";
    if (!trimmedId) {
      e.identificador = "El ID de solicitud Mercantil es requerido";
    } else if (
      trimmedId.length < LENGTH_RULES.identificador.min ||
      trimmedId.length > LENGTH_RULES.identificador.max
    ) {
      e.identificador = `Debe tener entre ${LENGTH_RULES.identificador.min} y ${LENGTH_RULES.identificador.max} caracteres`;
    }

    const solicitanteCedula = datos.solicitanteCedula?.trim() || "";
    if (!solicitanteCedula) {
      e.solicitanteCedula = "La cédula del solicitante/asegurado es requerida";
    } else if (
      solicitanteCedula.length < LENGTH_RULES.solicitanteCedula.min ||
      solicitanteCedula.length > LENGTH_RULES.solicitanteCedula.max
    ) {
      e.solicitanteCedula = `Debe tener entre ${LENGTH_RULES.solicitanteCedula.min} y ${LENGTH_RULES.solicitanteCedula.max} caracteres`;
    }

    if (!datos.tipo) e.tipo = "Seleccione el tipo de solicitud";

    if (idDuplicado) {
      e.identificador = "Este ID ya está registrado";
    }

    const titular = datos.titular?.trim() || "";
    if (!titular) {
      e.titular = "El nombre del titular es requerido";
    } else if (titular.length < LENGTH_RULES.titular.min) {
      e.titular = `Debe tener al menos ${LENGTH_RULES.titular.min} caracteres`;
    }

    const titularCedula = datos.titularCedula?.trim() || "";
    if (!titularCedula) {
      e.titularCedula = "La cédula del titular es requerida";
    } else if (
      titularCedula.length < LENGTH_RULES.titularCedula.min ||
      titularCedula.length > LENGTH_RULES.titularCedula.max
    ) {
      e.titularCedula = `Debe tener entre ${LENGTH_RULES.titularCedula.min} y ${LENGTH_RULES.titularCedula.max} caracteres`;
    }

    const solicitante = datos.solicitante?.trim() || "";
    if (!solicitante) {
      e.solicitante = "El nombre del solicitante es requerido";
    } else if (solicitante.length < LENGTH_RULES.solicitante.min) {
      e.solicitante = `Debe tener al menos ${LENGTH_RULES.solicitante.min} caracteres`;
    }

    const telefono = datos.telefono?.replace(/\D/g, "") || "";
    if (!telefono) {
      e.telefono = "El teléfono es requerido";
    } else if (telefono.length !== LENGTH_RULES.telefono.exact) {
      e.telefono = `Debe tener exactamente ${LENGTH_RULES.telefono.exact} dígitos`;
    }

    const correoElectronico = datos.correoElectronico?.trim() || "";
    if (!correoElectronico) {
      e.correoElectronico = "El correo electrónico es requerido";
    } else if (!correoElectronico.includes("@")) {
      e.correoElectronico = "El correo electrónico debe contener el símbolo @";
    } else if (!correoElectronico.includes(".")) {
      e.correoElectronico = "El correo electrónico debe contener un dominio válido";
    }

    setErrores(e);
    if (Object.keys(e).length) {
      const faltantes = Object.keys(e)
        .map((k) => NOMBRES_CAMPOS[k])
        .filter(Boolean)
        .join(", ");
      toast.warning(`Revisa los campos: ${faltantes}`);
      return false;
    }
    return true;
  };

  const handleSiguiente = () => {
    if (validar()) {
      onSiguiente();
    }
  };

  // Handler para seleccionar archivo CSV
  const handleSeleccionarCSV = (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setCsvFile(archivo);
    setResumenCSV(null);
    setAlertaCSVIdDuplicado(null);
  };

  // Handler para procesar y extraer datos del CSV
  const handleExtraerDatosCSV = async () => {
    if (!csvFile) {
      toast.warning("Seleccione un archivo CSV primero");
      return;
    }

    setCargandoCSV(true);
    try {
      // Validar archivo
      await validarArchivoCSV(csvFile);

      // Leer contenido
      const contenido = await leerArchivoComoTexto(csvFile);

      // Parsear CSV
      const datosExtraidos = parsearCSVSolicitud(contenido);

      // Verificar si el identificador ya existe
      if (datosExtraidos.identificador) {
        try {
          const { data } = await getsolicitudes(
            `query=${encodeURIComponent(datosExtraidos.identificador)}`,
          );
          const lista = data?.data;
          const coincidencias = Array.isArray(lista) && lista[0] ? lista[0] : [];
          if (Array.isArray(coincidencias) && coincidencias.length > 0) {
            const mensajeError = `El ID ${datosExtraidos.identificador} ya está registrado en el sistema. Use un CSV con un ID diferente o ingrese los datos manualmente.`;
            setAlertaCSVIdDuplicado({
              id: datosExtraidos.identificador,
              mensaje: mensajeError,
            });
            toast.error(mensajeError);
            setCargandoCSV(false);
            return;
          }
        } catch {
          // Error al verificar, continuamos
        }
      }

      // Actualizar datos en el formulario
      actualizarDatos({
        identificador: datosExtraidos.identificador,
        tipo: datosExtraidos.tipo,
        titular: datosExtraidos.titular,
        titularCedula: datosExtraidos.titularCedula,
        solicitante: datosExtraidos.solicitante,
        solicitanteCedula: datosExtraidos.solicitanteCedula,
        diagnostico: datosExtraidos.diagnostico,
        medicamentos: datosExtraidos.medicamentos,
      });

      // Marcar que se ingresó cédula para desbloquear campos
      if (datosExtraidos.solicitanteCedula) {
        setCedulaIngresada(true);
      }

      // Mostrar resumen
      setResumenCSV({
        totalMedicamentos: datosExtraidos._meta.totalMedicamentos,
        tieneDiagnostico: datosExtraidos._meta.tieneDiagnostico,
        tieneCedula: datosExtraidos._meta.tieneCedulaSolicitante,
      });

      toast.success(
        `Datos extraídos del CSV: ${datosExtraidos.identificador} - ${datosExtraidos._meta.totalMedicamentos} medicamentos cargados`
      );

      // Disparar búsqueda por cédula si existe
      if (datosExtraidos.solicitanteCedula) {
        setTimeout(() => {
          ejecutarBusqueda(datosExtraidos.solicitanteCedula);
        }, 100);
      }
    } catch (error) {
      const mensaje = error?.message || "Error al procesar el archivo CSV";
      toast.error(`Error: ${mensaje}`);
      setCsvFile(null);
    } finally {
      setCargandoCSV(false);
    }
  };

  // Handler para limpiar selección de CSV
  const handleLimpiarCSV = () => {
    setCsvFile(null);
    setResumenCSV(null);
    setAlertaCSVIdDuplicado(null);
  };

  return (
    <Card>
      <CardHeader>
        <h5 className="mb-0">
          <i className="ri-user-line me-2 text-primary"></i>
          Paso 1 — Datos del Paciente
        </h5>
      </CardHeader>
      <CardBody>
        <Row className="gy-3">
          {/* Sección de carga CSV */}
          <Col md={12}>
            <div className="border rounded p-3 bg-light mb-2">
              <h6 className="mb-3">
                <i className="ri-file-upload-line me-2 text-primary"></i>
                Cargar desde CSV (opcional)
              </h6>
              <Row className="align-items-end g-2">
                <Col md={6}>
                  <Label className="form-label small text-muted">
                    Archivo CSV de la orden de Seguros Mercantil
                  </Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleSeleccionarCSV}
                    disabled={cargandoCSV}
                  />
                  {csvFile && (
                    <small className="text-success d-block mt-1">
                      <i className="ri-check-line me-1"></i>
                      {csvFile.name}
                    </small>
                  )}
                </Col>
                <Col md={3}>
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleExtraerDatosCSV}
                    disabled={!csvFile || cargandoCSV}
                  >
                    {cargandoCSV ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <i className="ri-download-line me-1"></i>
                        Extraer datos
                      </>
                    )}
                  </button>
                </Col>
                <Col md={3}>
                  {csvFile && (
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={handleLimpiarCSV}
                      disabled={cargandoCSV}
                    >
                      <i className="ri-close-line me-1"></i>
                      Limpiar
                    </button>
                  )}
                </Col>
              </Row>
              {alertaCSVIdDuplicado && (
                <div className="alert alert-danger mt-3 mb-0 py-3 border-danger">
                  <div className="d-flex align-items-start gap-2">
                    <i className="ri-error-warning-line fs-4 text-danger"></i>
                    <div>
                      <strong className="d-block mb-1">ID ya registrado</strong>
                      <span className="d-block">{alertaCSVIdDuplicado.mensaje}</span>
                      <small className="text-muted d-block mt-1">
                        <i className="ri-information-line me-1"></i>
                        Para continuar, seleccione otro archivo CSV con un ID diferente o ingrese los datos manualmente sin usar CSV.
                      </small>
                    </div>
                  </div>
                </div>
              )}
              {resumenCSV && (
                <div className="alert alert-success mt-3 mb-0 py-2">
                  <small>
                    <i className="ri-check-double-line me-1"></i>
                    <strong>Datos extraídos:</strong>{" "}
                    {resumenCSV.totalMedicamentos} medicamento(s),{" "}
                    {resumenCSV.tieneDiagnostico ? "diagnóstico completo" : "sin diagnóstico"}
                    {resumenCSV.tieneCedula && ", cédula del asegurado"}
                    . Revise y complete los campos faltantes.
                  </small>
                </div>
              )}
              <small className="text-muted d-block mt-2">
                El archivo CSV debe tener el formato exportado desde el portal de Seguros Mercantil (26 columnas separadas por punto y coma).
              </small>
            </div>
          </Col>

          {/* ID de solicitud Mercantil */}
          <Col md={6}>
            <Label className="form-label fw-semibold">
              ID Solicitud Mercantil <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={datos.identificador || ""}
              onChange={handleIdentificadorChange}
              invalid={!!errores.identificador}
              placeholder="Ingrese el ID de Mercantil"
              maxLength={LENGTH_RULES.identificador.max}
            />
            <FormFeedback>{errores.identificador}</FormFeedback>
            {idMsg && (
              <small
                className={`d-block mt-1 ${idMsg.tipo === "ok" ? "text-success" : "text-danger"}`}
              >
                {idMsg.texto}
              </small>
            )}
            {verificandoId && (
              <small className="text-muted d-block">Verificando ID...</small>
            )}
            <small className="text-muted">
              Debe coincidir con el de la página de Seguros Mercantil.
            </small>
          </Col>

          {/* Tipo de solicitud */}
          <Col md={6}>
            <Label className="form-label fw-semibold">
              Tipo de Solicitud <span className="text-danger">*</span>
            </Label>
            <div className="d-flex gap-3">
              {["Aguda", "Cronica"].map((tipo) => (
                <div key={tipo} className="form-check form-check-inline">
                  <Input
                    type="radio"
                    className="form-check-input"
                    id={`tipo-${tipo}`}
                    name="tipo"
                    value={tipo}
                    checked={datos.tipo === tipo}
                    onChange={(e) => actualizarDatos({ tipo: e.target.value })}
                    disabled={camposBloqueados}
                  />
                  <Label className="form-check-label" htmlFor={`tipo-${tipo}`}>
                    {tipo === "Cronica" ? "Crónica" : tipo}
                  </Label>
                </div>
              ))}
            </div>
            {errores.tipo && (
              <div className="text-danger small mt-1">{errores.tipo}</div>
            )}
          </Col>

          {/* Cédula solicitante/asegurado — PRIMERO */}
          <Col md={6}>
            <Label className="form-label fw-semibold">
              Cédula del Solicitante / Asegurado{" "}
              <span className="text-danger">*</span>
            </Label>
            <div className="position-relative">
              <Input
                type="text"
                value={datos.solicitanteCedula}
                onChange={handleCedulaChange}
                invalid={!!errores.solicitanteCedula}
                placeholder="V-12345678"
                autoFocus
              />
              {buscando && (
                <Spinner
                  size="sm"
                  color="primary"
                  className="position-absolute"
                  style={{ top: 10, right: 12 }}
                />
              )}
            </div>
            <FormFeedback>{errores.solicitanteCedula}</FormFeedback>
            {busquedaMsg && (
              <small
                className={`mt-1 d-block ${
                  busquedaMsg.tipo === "ok"
                    ? "text-success"
                    : busquedaMsg.tipo === "error"
                      ? "text-danger"
                      : "text-warning"
                }`}
              >
                <i
                  className={`me-1 ${
                    busquedaMsg.tipo === "ok"
                      ? "ri-check-line"
                      : busquedaMsg.tipo === "error"
                        ? "ri-error-warning-line"
                        : "ri-information-line"
                  }`}
                ></i>
                {busquedaMsg.texto}
              </small>
            )}
          </Col>

          {/* Solicitante */}
          <Col md={6}>
            <Label className="form-label">
              Nombre del Solicitante <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={datos.solicitante}
              onChange={(e) => actualizarDatos({ solicitante: e.target.value })}
              invalid={!!errores.solicitante}
              placeholder="Nombre completo del solicitante"
              disabled={camposBloqueados}
            />
            <FormFeedback>{errores.solicitante}</FormFeedback>
          </Col>
          <Col md={6}>
            <Label className="form-label">
              Cédula del Titular <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={datos.titularCedula}
              onChange={(e) =>
                actualizarDatos({
                  titularCedula: e.target.value
                    .replace(/[^a-zA-Z0-9-]/g, "")
                    .slice(0, LENGTH_RULES.titularCedula.max),
                })
              }
              invalid={!!errores.titularCedula}
              placeholder="V-12345678"
              disabled={camposBloqueados}
              maxLength={LENGTH_RULES.titularCedula.max}
            />
            <FormFeedback>{errores.titularCedula}</FormFeedback>
          </Col>


          {/* Titular */}
          <Col md={6}>
            <Label className="form-label">
              Nombre del Titular <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={datos.titular}
              onChange={(e) => actualizarDatos({ titular: e.target.value })}
              invalid={!!errores.titular}
              placeholder="Nombre completo del titular"
              disabled={camposBloqueados}
            />
            <FormFeedback>{errores.titular}</FormFeedback>
          </Col>

          
          <Col md={6}>
            <Label className="form-label">
              Correo Electrónico <span className="text-danger">*</span>
            </Label>
            <Input
              type="email"
              value={datos.correoElectronico}
              onChange={(e) =>
                actualizarDatos({ correoElectronico: e.target.value })
              }
              invalid={!!errores.correoElectronico}
              placeholder="correo@ejemplo.com"
              disabled={camposBloqueados}
            />
            <FormFeedback>{errores.correoElectronico}</FormFeedback>
          </Col>

          {/* Contacto */}
          <Col md={6}>
            <Label className="form-label">
              Teléfono <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              value={datos.telefono}
              onChange={(e) =>
                actualizarDatos({
                  telefono: e.target.value
                    .replace(/\D/g, "")
                    .slice(0, LENGTH_RULES.telefono.exact),
                })
              }
              invalid={!!errores.telefono}
              placeholder="04141234567"
              maxLength={LENGTH_RULES.telefono.exact}
              disabled={camposBloqueados}
            />
            <FormFeedback>{errores.telefono}</FormFeedback>
          </Col>

          {/* La dirección de entrega se confirma durante gestión: la farmacia contacta al cliente */}
        </Row>

        <div className="d-flex justify-content-end mt-4">
          <button
            className="btn btn-primary"
            onClick={handleSiguiente}
            disabled={idDuplicado || !cedulaIngresada}
          >
            Siguiente <i className="ri-arrow-right-line ms-1"></i>
          </button>
        </div>
      </CardBody>
    </Card>
  );
};

PasoPaciente.propTypes = {
  datos: PropTypes.shape({
    identificador: PropTypes.string,
    solicitanteCedula: PropTypes.string,
    tipo: PropTypes.string,
    titular: PropTypes.string,
    titularCedula: PropTypes.string,
    solicitante: PropTypes.string,
    telefono: PropTypes.string,
    correoElectronico: PropTypes.string,
    direccion: PropTypes.string,
    recibe: PropTypes.string,
    diagnostico: PropTypes.string,
    medicamentos: PropTypes.arrayOf(
      PropTypes.shape({
        nombre: PropTypes.string,
        enRecipe: PropTypes.bool,
      })
    ),
    informe: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fechaInforme: PropTypes.string,
    recipe: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fechaRecipe: PropTypes.string,
    farmaciaAsignada: PropTypes.string,
    observaciones: PropTypes.string,
    capturaMedicamentos: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
  }).isRequired,
  actualizarDatos: PropTypes.func.isRequired,
  onSiguiente: PropTypes.func.isRequired,
};

export default PasoPaciente;
